import { AppData, Log } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { Helper } from "./helper";
import { MultiLangCC } from "./app-api-config";
import { ISO_LANGUAGE_MAP } from "./enum";

export const LANGUAGE_TO_ISO_MAP:any = {};

for (const [key, value] of Object.entries(ISO_LANGUAGE_MAP)) {
    LANGUAGE_TO_ISO_MAP[value] = key;
}

const CC_SETTINGS_KEY = 'cc-settings-v3'
const CC_KEY_MAP = ['captions', 'language', 'charColor', 'charOpacity', 'charSize', 'font', 'edge', 'winColor', 'winOpacity']
export class CCConfig {
    userConfig: CCSettingsOptionsType[] = defaultCCSettings()
    showSubtitle = false
    fontsLoaded = false
    appSetup!:AppSetup

    init(appSetup) {
        this.appSetup = appSetup
        this.loadSavedSettings(appSetup);
        this.loadFonts()
    }

    loadSavedSettings = (appSetup:AppSetup) => {
        const { appConfig } = appSetup

        // Try v3 (JSON) first
        const v3Raw = appConfig.useLocalStorage == true
            ? (localStorage.getItem(CC_SETTINGS_KEY) || '')
            : (Helper.getCookie(CC_SETTINGS_KEY) || '')

        if (v3Raw !== '') {
            try {
                const parsed = JSON.parse(v3Raw)
                CC_KEY_MAP.forEach((key, i) => {
                    if (parsed[key] !== undefined && this.userConfig[i]) {
                        this.userConfig[i]!.value = parsed[key]
                        if (this.userConfig[i]!.options) {
                            this.userConfig[i]!.options!.forEach(opt => {
                                opt.isActive = (opt.value === parsed[key])
                            })
                        }
                    }
                })
            } catch (e) {
                Log.info('cc-settings-v3 parse error, using defaults')
            }
        } else {
            // Migrate from v2 (pipe-delimited) if present
            const v2Raw = appConfig.useLocalStorage == true
                ? (localStorage.getItem('cc-settings-v2') || '')
                : (Helper.getCookie('cc-settings-v2') || '')
            if (v2Raw !== '') {
                this.migrateFromV2(v2Raw)
            }
        }

        // Clean up old cookie formats
        if (appConfig.useLocalStorage == true) {
            localStorage.removeItem('cc-settings')
            localStorage.removeItem('cc-settings-v2')
        } else {
            Helper.setGeneralCookie('cc-settings', '', 0)
            Helper.setGeneralCookie('cc-settings-v2', '', 0)
        }

        this.saveSettings()
        this.updateCSS()
    }

    migrateFromV2 = (raw: string) => {
        let ccValArr = raw.split('|')
        // Old format had RGB color at index 1; language was added later
        if (ccValArr[1] && ccValArr[1].includes(',')) {
            ccValArr.splice(1, 0, 'en')
        }
        const floatIndices = [3, 4, 8]
        for (let i = 0; i < ccValArr.length; i++) {
            if (!this.userConfig[i]) continue
            let val: any = ccValArr[i]
            if (i === 0) {
                val = (ccValArr[i] === 'true')
            } else if (floatIndices.includes(i)) {
                val = parseFloat(ccValArr[i])
                if (isNaN(val)) val = this.userConfig[i]!.value
            }
            this.userConfig[i]!.value = val
            if (this.userConfig[i]!.options) {
                this.userConfig[i]!.options!.forEach(opt => {
                    opt.isActive = (opt.value === val)
                })
            }
        }
        Log.info('cc-settings migrated from v2 to v3')
    }

    loadFonts() {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Courgette&family=Courier+Prime&family=Gothic+A1&family=Itim&family=Lora&family=Roboto&family=Ubuntu+Mono&display=swap';
        document.getElementsByTagName('head')[0].appendChild(link);
    }

    saveSettings() {
        const { appConfig } = this.appSetup
        const val: any = {}
        CC_KEY_MAP.forEach((key, i) => {
            val[key] = this.userConfig[i]!.value
        })
        const json = JSON.stringify(val)
        if (appConfig.useLocalStorage == true) {
            localStorage.setItem(CC_SETTINGS_KEY, json)
        } else {
            Helper.setGeneralCookie(CC_SETTINGS_KEY, json, 365)
        }
        Log.info('saveSettings ccValues:', val)
    }

    resetDefault() {
        const langConfig = this.userConfig.find(cfg => cfg.name === 'Language');
        const preservedLangOptions = langConfig?.options;

        this.userConfig = defaultCCSettings()

        if (preservedLangOptions) {
            const newLangConfig = this.userConfig.find(cfg => cfg.name === 'Language');
            if (newLangConfig) {
                newLangConfig.options = preservedLangOptions;
                newLangConfig.options.forEach(opt => {
                    opt.isActive = (opt.value === newLangConfig.value);
                });
            }
        }

        this.saveSettings()
        this.updateCSS()
    }

    updateLanguageOptions(tracks: MultiLangCC[] | null, itemDefaultLang?: string): string {
        const langIndex = this.userConfig.findIndex(cfg => cfg.name === 'Language');
        if (langIndex === -1) return 'en';
        const { appConfig } = this.appSetup;
        const savedRaw = appConfig?.useLocalStorage
            ? (localStorage.getItem(CC_SETTINGS_KEY) || '')
            : (Helper.getCookie(CC_SETTINGS_KEY) || '')
        let savedLang = ''
        try {
            savedLang = savedRaw ? (JSON.parse(savedRaw)?.language || '') : ''
        } catch (e) {
            savedLang = ''
        }
        const defaultLangIso = (itemDefaultLang && LANGUAGE_TO_ISO_MAP[itemDefaultLang.toLowerCase()]) || 'en';
        const defaultLangName = itemDefaultLang
            ? (itemDefaultLang.charAt(0).toUpperCase() + itemDefaultLang.slice(1))
            : 'English';
        const previousLang = savedLang || defaultLangIso;
        let resolvedLang = defaultLangIso;
        const options: CCSettingsOptionsValueType[] = [ { name: defaultLangName, value: defaultLangIso, isActive: false } ];
        const seenIsoCodes: Record<string, boolean> = { [defaultLangIso]: true };
        if (tracks && tracks.length > 0) {
            const supportedLanguages = appConfig?.supportedLanguages || [];
            for (const track of tracks) {
                if (!track?.language) continue;
                const lowerLang = track.language.toLowerCase();
                const isoCode = LANGUAGE_TO_ISO_MAP[lowerLang];

                if (isoCode && supportedLanguages.includes(isoCode) && !seenIsoCodes[isoCode]) {
                    seenIsoCodes[isoCode] = true;
                    const baseName = track.language.charAt(0).toUpperCase() + track.language.slice(1);
                    const displayName = track.aicc === true ? `${baseName} (AI)` : baseName;
                    options.push({
                        name: displayName,
                        value: isoCode,
                        isActive: false
                    });
                }
            }
        }

        if (options.some(o => o.value === previousLang)) {
            resolvedLang = previousLang;
        }

        options.forEach(o => { o.isActive = (o.value === resolvedLang); });
        this.userConfig[langIndex]!.options = options;
        this.userConfig[langIndex]!.value = resolvedLang;
        if (resolvedLang === previousLang) this.saveSettings();
        return resolvedLang;
    }

    updateCSS() {
        const { appState } = this.appSetup
        if (appState) {
            appState.showSubtitle = this.userConfig[0]!.value as boolean
        }
        const fontColor = this.userConfig[2]!.value as string
        const fontOpacity = this.userConfig[3]!.value as number
        const fontSize = this.userConfig[4]!.value as number
        const fontFace = this.userConfig[5]!.value as string
        const edge = this.userConfig[6]!.value as string
        const bgColor = this.userConfig[7]!.value as string
        const bgOpacity = this.userConfig[8]!.value as number
        const resolution = parseFloat((window.innerWidth/1920).toPrecision(7))

        const styleStr = `
                    background: rgba(${bgColor},${bgOpacity});
                    color: rgba(${fontColor},${fontOpacity});
                    font: ${Math.round(fontSize * resolution)}px ${fontFace};
                    text-shadow: ${edge};
                    padding: 10px 20px;
                `
        // console.log(styleStr)
        if (document.querySelector('#subtitle-cc-sample span')) {
            document.querySelector('#subtitle-cc-sample span').setAttribute('style', styleStr);
        }
        if (document.querySelector('#subtitle span')) {
            document.querySelector('#subtitle span').setAttribute('style', styleStr);
        }
        Log.info('cc style:', `
            bg-color: rgba(${bgColor},${bgOpacity});
            color: rgba(${fontColor},${fontOpacity});
            font-family: ${fontFace};
            font-size: ${fontSize};
            text-shadow: ${edge};
            padding: 10px 20px;
        `)
    }
}

export type CCSettingsOptionsType = {
    name: string,
    options: CCSettingsOptionsValueType[] | null,
    value: boolean | number | string | null
}

export type CCSettingsOptionsValueType = {
    name: string,
    value: boolean | number | string
    isActive: boolean
}

export const defaultCCSettings = (): CCSettingsOptionsType[] => {
    return [
        {
            'name': 'Captions', 'options': [
                { 'name': 'On', value: true, isActive: false },
                { 'name': 'Off', value: false, isActive: true }
            ], value: false
        },
        {
            'name': 'Language', 'options': [
                { 'name': 'English', value: 'en', isActive: true },
                // { 'name': 'Spanish', value: 'es', isActive: false },
            ], value: 'en'
        },
        {
            'name': 'Character Color', 'options': [
                { 'name': 'White', value: '255, 255, 255', isActive: true },
                { 'name': 'Black', value: '0, 0, 0', isActive: false },
                { 'name': 'Red', value: '255, 0, 0', isActive: false },
                { 'name': 'Green', value: '0, 255, 0', isActive: false },
                { 'name': 'Blue', value: '0, 0, 255', isActive: false },
                { 'name': 'Yellow', value: '255, 255, 0', isActive: false },
                { 'name': 'Magenta', value: '255, 0, 255', isActive: false },
                { 'name': 'Cyan', value: '0, 255, 255', isActive: false },
            ], value: '255, 255, 255'
        },
        {
            'name': 'Character Opacity', 'options': [
                { 'name': '75% Transparent', value: .25, isActive: false },
                { 'name': '50% Transparent', value: .5, isActive: false },
                { 'name': '25% Transparent', value: .75, isActive: false },
                { 'name': 'Solid', value: 1, isActive: true },
            ], value: 1
        },
        {
            'name': 'Character Size', 'options': [
                { 'name': 'Small', value: 24, isActive: false },
                { 'name': 'Medium', value: 28, isActive: false },
                { 'name': 'Standard', value: 36, isActive: true },
                { 'name': 'Large', value: 42, isActive: false },
                { 'name': 'Extra-Large', value: 48, isActive: false },
            ], value: 36
        },
        {
            'name': 'Font', 'options': [
                { 'name': 'Default', value: 'Times', isActive: true },
                { 'name': 'Courier', value: 'Courier Prime', isActive: false },
                { 'name': 'Lora', value: 'Lora', isActive: false },
                { 'name': 'Mono', value: 'Ubuntu Mono', isActive: false },
                { 'name': 'Roboto', value: 'Roboto', isActive: false },
                { 'name': 'Casual', value: 'Itim', isActive: false },
                { 'name': 'Courgette', value: 'Courgette', isActive: false },
                { 'name': 'Small Caps', value: 'Gothic A1', isActive: false }
            ], value: 'Times'
        },
        {
            'name': 'Caption Edge Attribute', 'options': [
                { 'name': 'No Edge', value: 'none', isActive: true },
                { 'name': 'Raised', value: '0px 2px 0px #666, 0px 4px 0px #666', isActive: false },
                { 'name': 'Depressed', value: '0px -2px 0px #999, 0px 2px 0px #999', isActive: false },
                { 'name': 'Uniform', value: '-2px 0px 0px #999, 2px 0px 0px #999', isActive: false },
                { 'name': 'Drop Shadow', value: '0px 0px 5px #333, 0px 0px 5px #333, 0px 0px 5px #333, 0px 0px 5px #333', isActive: false }
            ], value: 'none'
        },
        {
            'name': 'Caption Window Color', 'options': [
                { 'name': 'White', value: '255, 255, 255', isActive: false },
                { 'name': 'Black', value: '0, 0, 0', isActive: true },
                { 'name': 'Red', value: '255, 0, 0', isActive: false },
                { 'name': 'Green', value: '0, 255, 0', isActive: false },
                { 'name': 'Blue', value: '0, 0, 255', isActive: false },
                { 'name': 'Yellow', value: '255, 255, 0', isActive: false },
            ], value: '0, 0, 0'
        },
        {
            'name': 'Caption Window Opacity', 'options': [
                { 'name': '100% Transparent', value: 0, isActive: false },
                { 'name': '75% Transparent', value: .25, isActive: false },
                { 'name': '50% Transparent', value: .5, isActive: false },
                { 'name': '25% Transparent', value: .75, isActive: false },
                { 'name': 'Solid', value: 1, isActive: true },
            ], value: 1
        },
        {
            'name': 'Reset Default Caption Settings', 'options': null, value: null
        }
    ]
}
